import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, CreditCard, Clock, CheckCircle, AlertCircle, Euro, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInstallmentsPage = () => {
  const [installments, setInstallments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/installments/list?status=${filter}`, { withCredentials: true }),
        axios.get(`${API}/admin/installments/stats`, { withCredentials: true }),
      ]);
      setInstallments(listRes.data.installments || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const markPaid = async (userId, num) => {
    if (!window.confirm(`Confirmer le paiement du prélèvement #${num} ?`)) return;
    try {
      await axios.post(
        `${API}/admin/installments/${userId}_${num}/mark-paid`,
        {},
        { withCredentials: true }
      );
      toast.success('Prélèvement marqué comme payé');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  const isOverdue = (iso) => new Date(iso) < new Date();

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" data-testid="installments-back-btn">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <h1 className="text-lg font-bold text-slate-900">Paiements 4x - Admin</h1>
          <Button variant="ghost" size="sm" onClick={fetchData} data-testid="installments-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card data-testid="stat-pending">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 uppercase font-semibold">En attente</p>
                  <Clock className="h-4 w-4 text-amber-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats.pending}</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-overdue" className={stats.overdue > 0 ? 'border-red-300' : ''}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 uppercase font-semibold">En retard</p>
                  <AlertCircle className={`h-4 w-4 ${stats.overdue > 0 ? 'text-red-500' : 'text-slate-400'}`} />
                </div>
                <p className={`text-2xl font-bold ${stats.overdue > 0 ? 'text-red-600' : 'text-slate-900'}`}>{stats.overdue}</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-paid">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 uppercase font-semibold">Payés</p>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats.paid}</p>
              </CardContent>
            </Card>
            <Card data-testid="stat-amount">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500 uppercase font-semibold">À encaisser</p>
                  <Euro className="h-4 w-4 text-[#0D47A1]" />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stats.pending_amount_eur.toLocaleString('fr-FR')}€</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2">
          {['pending', 'paid', 'all'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
            >
              {f === 'pending' ? 'En attente' : f === 'paid' ? 'Payés' : 'Tous'}
            </Button>
          ))}
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CreditCard className="h-5 w-5" />
              Prélèvements 4x
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-500 py-8">Chargement...</p>
            ) : installments.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Aucun prélèvement à afficher.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-500">
                      <th className="text-left py-2 px-2">Client</th>
                      <th className="text-left py-2 px-2">Email</th>
                      <th className="text-center py-2 px-2">#</th>
                      <th className="text-right py-2 px-2">Montant</th>
                      <th className="text-left py-2 px-2">Échéance</th>
                      <th className="text-left py-2 px-2">Statut</th>
                      <th className="text-right py-2 px-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installments.map((inst, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`installment-row-${i}`}>
                        <td className="py-3 px-2 font-medium text-slate-900">{inst.user_name}</td>
                        <td className="py-3 px-2 text-slate-600 text-xs">{inst.user_email_current}</td>
                        <td className="py-3 px-2 text-center">{inst.installment_number}/4</td>
                        <td className="py-3 px-2 text-right font-mono">{(inst.amount_cents / 100).toFixed(2)}€</td>
                        <td className="py-3 px-2">
                          <span className={isOverdue(inst.due_date) && inst.status === 'pending' ? 'text-red-600 font-semibold' : ''}>
                            {formatDate(inst.due_date)}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          {inst.status === 'paid' ? (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Payé</Badge>
                          ) : isOverdue(inst.due_date) ? (
                            <Badge className="bg-red-100 text-red-800 hover:bg-red-100">En retard</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">En attente</Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right">
                          {inst.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => markPaid(inst.user_id, inst.installment_number)}
                              className="bg-[#0D47A1] hover:bg-[#0A367A]"
                              data-testid={`mark-paid-btn-${i}`}
                            >
                              Marquer payé
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-xs text-slate-500 text-center py-4">
          <p>💡 Après avoir créé un prélèvement dans <a href="https://squareup.com/dashboard/invoices/overview" target="_blank" rel="noopener noreferrer" className="underline">Square Dashboard</a>, marquez-le comme payé ici pour tenir vos comptes à jour.</p>
        </div>
      </main>
    </div>
  );
};

export default AdminInstallmentsPage;
